/** Pure derivations for the Spend page, the session cost chip and Overview's budget alerts. */
import type { Budget, BudgetStatus, CostsState, Id, SessionUsage, TokenCounts } from '@orgus/protocol';
import { formatUsd } from '@orgus/protocol';
import { formatCompactNumber, formatDate, formatPercent } from '@orgus/ui';
import type { StatusTone, Tone } from '@orgus/ui';

/** Cache reads over every input token: how much of the prompt came cheap. 0..1, null with no input. */
export function cacheHitRatio(t: Pick<TokenCounts, 'input' | 'cacheRead' | 'cacheWrite'>): number | null {
  const all = t.input + t.cacheRead + t.cacheWrite;
  return all > 0 ? t.cacheRead / all : null;
}

export type BudgetState = BudgetStatus['state'];

/**
 * Where a budget stands, spend and forecast together: the one answer the Spend page, Overview,
 * the phone and the wall all show. The hub's `state` only looks at what is already spent, so a
 * budget on course to blow its limit still reads `ok` there; this never calls that "On track".
 *   over     spent reached the limit (hard)
 *   near     spent passed the warning point (soft)
 *   forecast spent is fine, but the period-end forecast crosses the limit
 *   ok       neither
 */
export type ForecastState = 'ok' | 'forecast' | 'near' | 'over';

export const FORECAST_STATE_LABEL: Record<ForecastState, string> = { ok: 'On track', forecast: 'Forecast over', near: 'Near limit', over: 'Over limit' };
export const FORECAST_STATE_TONE: Record<ForecastState, StatusTone> = { ok: 'passed', forecast: 'waiting', near: 'waiting', over: 'failed' };

/** The forecast state of one budget. A forecast at or over the limit is never `ok`. */
export function forecastState(s: Pick<BudgetStatus, 'state'> & Partial<Pick<BudgetStatus, 'forecast' | 'limit' | 'forecastOver'>>): ForecastState {
  if (s.state === 'hard') return 'over';
  if (s.state === 'soft') return 'near';
  const crosses = s.limit != null && s.forecast != null && s.limit > 0 && s.forecast >= s.limit;
  return s.forecastOver || crosses ? 'forecast' : 'ok';
}

/** Label and tone of a budget's forecast state, ready for a badge or a tile. */
export function forecastStatus(s: Parameters<typeof forecastState>[0]): { state: ForecastState; label: string; tone: StatusTone } {
  const state = forecastState(s);
  return { state, label: FORECAST_STATE_LABEL[state], tone: FORECAST_STATE_TONE[state] };
}

/**
 * Labels for the hub's spend-only state. Prefer `forecastStatus`, which never says "On track"
 * while the forecast is over the limit; this stays for code that has no forecast to hand.
 */
export const BUDGET_STATE_LABEL: Record<BudgetState, string> = { ok: 'On track', soft: 'Near limit', hard: 'Over limit' };

/** A budget's one status label from the hub's state and its forecast flag: the forecast state's label. */
export function budgetStateLabel(state: BudgetState, forecastOver: boolean): string {
  return FORECAST_STATE_LABEL[forecastState({ state, forecastOver })];
}
export const BUDGET_TONE: Record<BudgetState, Tone> = { ok: 'passed', soft: 'waiting', hard: 'failed' };

export const PERIOD_LABEL: Record<Budget['period'], string> = { day: 'Daily', month: 'Monthly', total: 'Total' };

/** "Ends Oct 31", "Resets tonight", "No end". */
export function periodEndLabel(s: Pick<BudgetStatus, 'periodEnd'>, now: number): string {
  if (s.periodEnd === null) return 'No end date';
  const left = s.periodEnd - now;
  if (left <= 24 * 3600_000) return 'Resets at midnight';
  const d = new Date(s.periodEnd - 1);
  return `Ends ${formatDate(d.getTime(), d.getTime())}`;
}

/** Fill and markers of a burn bar, as fractions of the bar (which runs to max(limit, forecast) when over). */
export function burnGeometry(s: Pick<BudgetStatus, 'spent' | 'limit' | 'forecast'>, softAt: number): { spent: number; forecast: number; soft: number; limit: number } {
  const span = Math.max(s.limit, s.spent, s.forecast) || 1;
  return { spent: s.spent / span, forecast: Math.min(1, s.forecast / span), soft: (softAt * s.limit) / span, limit: s.limit / span };
}

/** Session id to its usage, for the Sessions list chip. */
export function sessionCostIndex(c: CostsState | undefined): Record<Id, SessionUsage> {
  const out: Record<Id, SessionUsage> = {};
  for (const s of c?.sessions ?? []) out[s.sessionId] = s;
  return out;
}

export interface BudgetNeed {
  key: string;
  title: string;
  detail: string;
  at: number;
  state: 'soft' | 'hard' | 'runaway';
  to: { path: string } | { session: string };
}

/** Budgets near or over their limit, and runaway sessions: Overview's "Needs you" rows. */
export function budgetNeeds(c: CostsState | undefined): BudgetNeed[] {
  if (!c) return [];
  const out: BudgetNeed[] = [];
  for (const s of c.budgetStatus) {
    if (s.state === 'ok') continue;
    const b = c.budgets.find((x) => x.id === s.budgetId);
    const stops = b?.onLimit === 'stop';
    out.push({
      key: `b:${s.budgetId}`,
      title:
        s.state === 'hard'
          ? `${s.label} is over its ${formatUsd(s.limit)} budget`
          : `${s.label} is at ${formatPercent(s.spent / s.limit)} of its ${formatUsd(s.limit)} budget`,
      detail:
        s.state === 'hard'
          ? `${formatUsd(s.spent)} spent.${stops ? ' New and launched sessions are stopped until you raise it.' : ' Orgus only warns for this budget.'}`
          : `${formatUsd(s.spent)} spent, ${formatUsd(s.forecast)} forecast by the end of the period.`,
      at: c.updatedAt,
      state: s.state,
      to: { path: `/spend?budget=${encodeURIComponent(s.budgetId)}` },
    });
  }
  for (const r of c.runaways) {
    if (!r.active) continue;
    out.push({
      key: `r:${r.sessionId}`,
      title: `${r.title} is burning tokens ${Math.max(1, Math.round(r.rate / Math.max(1, r.baseline)))}x faster than usual`,
      detail: `${formatTokensPerMin(r.rate)} now, against ${formatTokensPerMin(r.baseline)} usually. A retry loop or a stuck tool call looks like this.`,
      at: r.at,
      state: 'runaway',
      to: { session: r.sessionId },
    });
  }
  const rank = { hard: 0, runaway: 1, soft: 2 };
  return out.sort((a, b) => rank[a.state] - rank[b.state] || b.at - a.at);
}

/** "1.2M tokens/min". */
export function formatTokensPerMin(n: number): string {
  return `${formatCompactNumber(Math.round(n))} tokens/min`;
}

/** "4.2M tokens", "820 tokens". */
export function formatTokens(n: number): string {
  return `${formatCompactNumber(Math.round(n))} tokens`;
}

/** Strip the price variant from a usage key: 'claude-opus-5-5#fast' gives 'claude-opus-5-5'. */
export function baseModel(key: string): string {
  const i = key.indexOf('#');
  return i < 0 ? key : key.slice(0, i);
}

/** Share of the month's spend per bucket (0..1), keeping order. */
export function shares<T extends { cost: number }>(list: T[]): (T & { share: number })[] {
  const total = list.reduce((n, b) => n + b.cost, 0);
  return list.map((b) => ({ ...b, share: total > 0 ? b.cost / total : 0 }));
}

/** Keep the first n, fold the rest into one "Other" bucket. */
export function topWithOther<T extends { key: string; label: string; cost: number; tokens: number; sessions: number }>(list: T[], n: number): T[] {
  if (list.length <= n) return list;
  const head = list.slice(0, n - 1);
  const rest = list.slice(n - 1);
  const other = rest.reduce(
    (o, b) => ({ ...o, cost: o.cost + b.cost, tokens: o.tokens + b.tokens, sessions: o.sessions + b.sessions }),
    { ...rest[0]!, key: '__other', label: `${rest.length} more`, cost: 0, tokens: 0, sessions: 0 },
  );
  return [...head, other];
}

/** The day label under a daily bar: 'Oct 16'. */
export function dayLabel(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const t = new Date(y!, (m ?? 1) - 1, d ?? 1).getTime();
  return formatDate(t, t);
}

/** What a budget is about, for the dialog's scope picker. */
export function scopeKey(b: Pick<Budget, 'scope'>): string {
  const s = b.scope;
  return s.kind === 'org' ? 'org' : s.kind === 'project' ? `project:${s.projectId}` : s.kind === 'session' ? `session:${s.sessionId}` : `mission:${s.missionId}`;
}

export function scopeFromKey(key: string): Budget['scope'] | null {
  if (key === 'org') return { kind: 'org' };
  const i = key.indexOf(':');
  const kind = key.slice(0, i);
  const id = key.slice(i + 1);
  if (!id) return null;
  if (kind === 'project') return { kind, projectId: id };
  if (kind === 'session') return { kind, sessionId: id };
  if (kind === 'mission') return { kind, missionId: id };
  return null;
}

/** A budget limit typed by a person: '$1,200.50' or '1200.5'. Null when not a positive amount. */
export function parseLimit(text: string): number | null {
  const n = Number(text.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) && n > 0 && n <= 10_000_000 ? Math.round(n * 100) / 100 : null;
}

/** The warning point typed as a percent: '80' or '80%'. Null outside 1..99. */
export function parseSoftPercent(text: string): number | null {
  const n = Number(text.replace(/[%\s]/g, ''));
  return Number.isFinite(n) && n >= 1 && n <= 99 ? n / 100 : null;
}

/** This month's running total by day, for the burn line: one point per day so far, and the days in the month. */
export function monthBurn(c: Pick<CostsState, 'byDay' | 'monthStart'>): { actual: number[]; days: number; dayLabel: (i: number) => string } {
  const start = new Date(c.monthStart);
  const y = start.getFullYear();
  const m = start.getMonth();
  const first = `${y}-${String(m + 1).padStart(2, '0')}-01`;
  const days = new Date(y, m + 1, 0).getDate();
  const actual: number[] = [];
  let total = 0;
  for (const b of c.byDay) {
    if (b.key < first) continue;
    total += b.cost;
    actual.push(total);
  }
  return {
    actual,
    days,
    dayLabel: (i) => {
      const t = new Date(y, m, i + 1).getTime();
      return formatDate(t, t);
    },
  };
}

/**
 * The one reading of today's and this month's spend (QA-1): the Spend hero and the Sessions
 * glance tile both show these figures, so they never disagree. Today is the hub's local day
 * (the last `byDay` bucket, the same day `today` sums); `month` counts sessions with spend this
 * month; `reading` counts sessions whose transcripts are still being read, so both surfaces
 * can say the figures are still climbing.
 */
export interface SpendSummary {
  today: number;
  todaySessions: number;
  month: number;
  monthSessions: number;
  reading: number;
}

export function spendSummary(c: Pick<CostsState, 'today' | 'month' | 'byDay' | 'sessions'>): SpendSummary {
  const last = c.byDay[c.byDay.length - 1];
  return {
    today: c.today,
    todaySessions: last && last.tokens ? last.sessions : 0,
    month: c.month,
    monthSessions: c.sessions.length,
    reading: c.sessions.filter((s) => s.partial).length,
  };
}
